import { ImageZoom } from '../components/ImageZoom';
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { questionsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { HighlightableText } from '../components/HighlightableText';
import { ExplanationRenderer } from '../components/core/ExplanationRenderer';
import { SUBJECTS, ORGAN_SYSTEMS, SUBJECT_COLORS, SYSTEM_COLORS, DIFFICULTY_COLORS, cn } from '../lib/utils';
import { useTheme } from '../contexts/ThemeContext';
import { toast } from 'sonner';
import {
    Loader2,
    CheckCircle,
    XCircle,
    RefreshCw,
    ArrowRight,
    Clock,
    Lightbulb,
    BookOpen,
    Layers,
    Activity
} from 'lucide-react';
import Layout from '../components/Layout';

export default function Practice() {
    const { isDark } = useTheme();
    const [searchParams, setSearchParams] = useSearchParams();
    const [practiceMode, setPracticeMode] = useState('subject'); // 'subject' or 'system'
    const [subject, setSubject] = useState(searchParams.get('subject') || '');
    const [organSystem, setOrganSystem] = useState('');
    const [difficulty, setDifficulty] = useState('medium');
    const [question, setQuestion] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [lockedAnswer, setLockedAnswer] = useState(null);
    const [result, setResult] = useState(null);
    const [strikethrough, setStrikethrough] = useState([]);
    const [startTime, setStartTime] = useState(null);
    const [elapsed, setElapsed] = useState(0);
    const [loadingImage, setLoadingImage] = useState(false);

    // Poll for background-generated image
    useEffect(() => {
        if (!question) return;
        if (question.image_base64) return; // already have image
        if (!question.image_description) return; // no image expected
        if (question.image_ready) return; // confirmed no image

        setLoadingImage(true);
        let cancelled = false;
        const poll = async () => {
            for (let i = 0; i < 20; i++) { // poll up to ~40 seconds
                if (cancelled) return;
                await new Promise(r => setTimeout(r, 2000));
                if (cancelled) return;
                try {
                    const res = await questionsAPI.getImage(question.id);
                    if (res.data.status === 'ready' && res.data.image_base64) {
                        if (!cancelled) {
                            setQuestion(prev => prev?.id === question.id ? { ...prev, image_base64: res.data.image_base64, image_ready: true } : prev);
                            setLoadingImage(false);
                        }
                        return;
                    }
                    if (res.data.status === 'no_image') {
                        setLoadingImage(false);
                        return;
                    }
                } catch { /* continue polling */ }
            }
            if (!cancelled) setLoadingImage(false);
        };
        poll();
        return () => { cancelled = true; };
    }, [question?.id, question?.image_base64, question?.image_description, question?.image_ready]);

    // Timer effect
    useEffect(() => {
        let interval;
        if (startTime && !submitted) {
            interval = setInterval(() => {
                setElapsed(Math.floor((Date.now() - startTime) / 1000));
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [startTime, submitted]);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const generateQuestion = async () => {
        if (practiceMode === 'subject' && !subject) {
            toast.error('Please select a subject');
            return;
        }
        if (practiceMode === 'system' && !organSystem) {
            toast.error('Please select an organ system');
            return;
        }

        setLoading(true);
        setQuestion(null);
        setSelectedAnswer(null);
        setSubmitted(false);
        setSubmitting(false);
        setLockedAnswer(null);
        setResult(null);
        setStrikethrough([]);
        setStartTime(null);
        setElapsed(0);

        try {
            // For system-based practice, use Pathology as default subject with the organ system
            const subjectToUse = practiceMode === 'system' ? 'Pathology' : subject;
            const systemToUse = practiceMode === 'system' ? organSystem : null;
            
            const response = await questionsAPI.generate(subjectToUse, difficulty, systemToUse);
            setQuestion(response.data);
            setStartTime(Date.now());
        } catch (err) {
            console.error('Failed to generate question:', err);
            toast.error(err.response?.data?.detail || 'Failed to generate question. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const submitAnswer = async () => {
        if (!selectedAnswer || !question || submitting) return;

        const answeredWith = selectedAnswer;
        setLockedAnswer(answeredWith);
        setSubmitting(true);

        try {
            const response = await questionsAPI.submitAnswer(question.id, {
                question_id: question.id,
                selected_answer: answeredWith,
                time_spent_seconds: elapsed
            });
            setResult(response.data);
            setSubmitted(true);
            
            if (response.data.is_correct) {
                toast.success('Correct! Well done!');
            } else {
                toast.error('Incorrect. Review the explanation below.');
            }
        } catch (err) {
            console.error('Failed to submit answer:', err);
            const detail = err.response?.data?.detail || err.message || 'Unknown error';
            toast.error(`Failed to submit answer: ${detail}. Click "Submit" to try again.`);
            setSubmitting(false);
            setLockedAnswer(null);
        }
    };

    const toggleStrikethrough = (letter) => {
        if (submitted) return;
        setStrikethrough(prev => 
            prev.includes(letter) 
                ? prev.filter(l => l !== letter)
                : [...prev, letter]
        );
    };

    const handleSubjectChange = (value) => {
        setSubject(value);
        setSearchParams(value ? { subject: value } : {});
    };

    const subjectColors = subject ? SUBJECT_COLORS[subject] : null;
    const systemColors = organSystem ? SYSTEM_COLORS[organSystem] : null;
    const difficultyColors = DIFFICULTY_COLORS[difficulty];

    return (
        <Layout>
            <div className="max-w-4xl mx-auto space-y-6 animate-fade-in" data-testid="practice-container">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                            Practice Questions
                        </h1>
                        <p className="text-slate-500 mt-1">Generate NBME-style questions and test your knowledge</p>
                    </div>
                </div>

                {/* Controls */}
                <Card className="border-slate-200">
                    <CardContent className="p-6 space-y-4">
                        {/* Mode Selection */}
                        <Tabs value={practiceMode} onValueChange={setPracticeMode}>
                            <TabsList className="bg-slate-100 w-full sm:w-auto">
                                <TabsTrigger value="subject" className="flex-1 sm:flex-none">
                                    <BookOpen className="h-4 w-4 mr-2" />
                                    By Subject
                                </TabsTrigger>
                                <TabsTrigger value="system" className="flex-1 sm:flex-none">
                                    <Activity className="h-4 w-4 mr-2" />
                                    By System (CAS)
                                </TabsTrigger>
                            </TabsList>
                        </Tabs>

                        <div className="flex flex-col sm:flex-row gap-4 items-end">
                            {practiceMode === 'subject' ? (
                                <div className="flex-1 space-y-2">
                                    <label className="text-sm font-medium text-slate-700">Subject</label>
                                    <Select value={subject} onValueChange={handleSubjectChange}>
                                        <SelectTrigger data-testid="subject-select">
                                            <SelectValue placeholder="Select a subject" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {SUBJECTS.map(s => (
                                                <SelectItem key={s} value={s}>{s}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            ) : (
                                <div className="flex-1 space-y-2">
                                    <label className="text-sm font-medium text-slate-700">Organ System</label>
                                    <Select value={organSystem} onValueChange={setOrganSystem}>
                                        <SelectTrigger data-testid="system-select">
                                            <SelectValue placeholder="Select an organ system" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {ORGAN_SYSTEMS.map(s => (
                                                <SelectItem key={s} value={s}>{s}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}
                            <div className="flex-1 space-y-2">
                                <label className="text-sm font-medium text-slate-700">Difficulty</label>
                                <Select value={difficulty} onValueChange={setDifficulty}>
                                    <SelectTrigger data-testid="difficulty-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="easy">Easy</SelectItem>
                                        <SelectItem value="medium">Medium</SelectItem>
                                        <SelectItem value="hard">Hard</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <Button 
                                onClick={generateQuestion} 
                                disabled={loading || (practiceMode === 'subject' ? !subject : !organSystem)}
                                className="w-full sm:w-auto"
                                data-testid="generate-question-btn"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Generating...
                                    </>
                                ) : (
                                    <>
                                        <RefreshCw className="mr-2 h-4 w-4" />
                                        Generate Question
                                    </>
                                )}
                            </Button>
                        </div>

                        {practiceMode === 'system' && (
                            <p className="text-sm text-slate-500 flex items-center gap-2">
                                <Layers className="h-4 w-4" />
                                System-based questions are ideal for CAS (Clinical Assessment/Shelf) exam preparation
                            </p>
                        )}
                    </CardContent>
                </Card>

                {/* Loading State */}
                {loading && (
                    <Card className="border-slate-200">
                        <CardContent className="p-8">
                            <div className="flex flex-col items-center justify-center space-y-4">
                                <div className="relative">
                                    <div className="w-16 h-16 border-4 border-indigo-100 rounded-full"></div>
                                    <div className="absolute top-0 left-0 w-16 h-16 border-4 border-indigo-600 rounded-full border-t-transparent animate-spin"></div>
                                </div>
                                <p className="text-slate-600 font-medium">Generating your question...</p>
                                <p className="text-sm text-slate-500">
                                    {(practiceMode === 'subject' && ['Histology', 'Pathology', 'Anatomy', 'Microbiology'].includes(subject)) ||
                                     (practiceMode === 'system' && ['Cardiovascular', 'Respiratory', 'Dermatology', 'Hematology/Oncology', 'Musculoskeletal'].includes(organSystem))
                                        ? 'Generating question with medical image (may take 30-60 seconds)...'
                                        : 'This may take a few seconds'}
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Question Display */}
                {question && !loading && (
                    <div className="space-y-6">
                        {/* Question Header */}
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3 flex-wrap">
                                {question.subject && SUBJECT_COLORS[question.subject] && (
                                    <Badge className={cn(
                                        SUBJECT_COLORS[question.subject].bg, 
                                        SUBJECT_COLORS[question.subject].text, 
                                        SUBJECT_COLORS[question.subject].border
                                    )}>
                                        {question.subject}
                                    </Badge>
                                )}
                                {question.organ_system && SYSTEM_COLORS[question.organ_system] && (
                                    <Badge className={cn(
                                        SYSTEM_COLORS[question.organ_system].bg,
                                        SYSTEM_COLORS[question.organ_system].text
                                    )}>
                                        {question.organ_system}
                                    </Badge>
                                )}
                                <Badge className={cn(difficultyColors.bg, difficultyColors.text)}>
                                    {difficulty.charAt(0).toUpperCase() + difficulty.slice(1)}
                                </Badge>
                            </div>
                            <div className="flex items-center gap-2 text-slate-500">
                                <Clock className="h-4 w-4" />
                                <span className="font-mono" data-testid="timer">{formatTime(elapsed)}</span>
                            </div>
                        </div>

                        {/* Vignette with optional image */}
                        <Card className="border-slate-200 bg-slate-50">
                            <CardContent className="p-6">
                                {/* Medical Image - shown without description until answered */}
                                {question.image_base64 && (
                                    <div className="mb-4">
                                        <div className="relative rounded-lg overflow-hidden border-2 border-slate-300 bg-white animate-in fade-in duration-500">
                                            <ImageZoom 
                                                src={`data:image/png;base64,${question.image_base64}`}
                                                alt="Medical image for examination"
                                            />
                                        </div>
                                        {/* Only show image description AFTER answer is submitted */}
                                        {submitted && question.image_description && (
                                            <div className="mt-3 p-3 rounded-lg bg-indigo-50 border border-indigo-200">
                                                <p className="text-sm text-indigo-800 font-medium mb-1">Image Description:</p>
                                                <p className="text-sm text-indigo-700">
                                                    {question.image_description}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                )}
                                {/* Image loading shimmer */}
                                {!question.image_base64 && loadingImage && (
                                    <div className="mb-4">
                                        <div className="relative rounded-lg overflow-hidden border-2 border-slate-200 bg-slate-100 h-48 flex items-center justify-center">
                                            <div className="flex flex-col items-center gap-2 text-slate-400">
                                                <Loader2 className="h-6 w-6 animate-spin" />
                                                <span className="text-sm">Loading medical image...</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <div className="vignette-text" data-testid="question-vignette">
                                    <HighlightableText 
                                        text={question.vignette} 
                                        className="text-slate-700 leading-relaxed"
                                    />
                                </div>
                            </CardContent>
                        </Card>

                        {/* Question Stem */}
                        <Card className="border-slate-200">
                            <CardContent className="p-6">
                                <p className="text-lg font-medium text-slate-900 mb-6" data-testid="question-stem">
                                    {question.question_stem}
                                </p>

                                {/* Options */}
                                <div className="space-y-3">
                                    {question.options.map((option) => {
                                        const answered = lockedAnswer || selectedAnswer;
                                        const isLocked = submitted || submitting;
                                        const isSelected = answered === option.letter;
                                        const isCorrect = submitted && option.letter === question.correct_answer;
                                        const isWrong = submitted && isSelected && !isCorrect;
                                        const isStruckThrough = strikethrough.includes(option.letter);

                                        return (
                                            <div
                                                key={option.letter}
                                                onClick={() => !isLocked && setSelectedAnswer(option.letter)}
                                                onContextMenu={(e) => {
                                                    e.preventDefault();
                                                    toggleStrikethrough(option.letter);
                                                }}
                                                className={cn(
                                                    "question-option",
                                                    isDark ? "border-slate-600" : "",
                                                    !isLocked && !isDark && "hover:border-indigo-300 hover:bg-indigo-50/50",
                                                    !isLocked && isDark && "hover:border-indigo-500 hover:bg-indigo-900/30",
                                                    isSelected && !submitted && !isDark && "border-indigo-500 bg-indigo-50",
                                                    isSelected && !submitted && isDark && "border-indigo-500 bg-indigo-900/40",
                                                    isCorrect && !isDark && "border-emerald-500 bg-emerald-100",
                                                    isCorrect && isDark && "border-emerald-500 bg-emerald-900/60",
                                                    isWrong && !isDark && "border-red-500 bg-red-100",
                                                    isWrong && isDark && "border-red-500 bg-red-900/60",
                                                    isStruckThrough && !isLocked && "opacity-50",
                                                    isLocked && !isSelected && !isCorrect && "opacity-60"
                                                )}
                                                data-testid={`option-${option.letter}`}
                                            >
                                                <div className={cn(
                                                    "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm",
                                                    !submitted && isSelected && "bg-indigo-600 text-white",
                                                    !submitted && !isSelected && !isDark && "bg-slate-200 text-slate-600",
                                                    !submitted && !isSelected && isDark && "bg-slate-700 text-slate-300",
                                                    isCorrect && "bg-emerald-600 text-white",
                                                    isWrong && "bg-red-600 text-white"
                                                )}>
                                                    {submitted && isCorrect && <CheckCircle className="h-5 w-5" />}
                                                    {submitted && isWrong && <XCircle className="h-5 w-5" />}
                                                    {!submitted && option.letter}
                                                    {submitted && !isCorrect && !isWrong && option.letter}
                                                </div>
                                                <span className={cn(
                                                    "option-text flex-1",
                                                    isStruckThrough && !submitted && "line-through text-slate-400",
                                                    isCorrect && "text-emerald-900 font-medium",
                                                    isWrong && "text-red-900",
                                                    !isCorrect && !isWrong && isDark && "text-slate-200"
                                                )}>
                                                    {option.text}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Tip */}
                                {!submitted && (
                                    <p className="mt-4 text-sm text-slate-500 flex items-center gap-2">
                                        <Lightbulb className="h-4 w-4" />
                                        Tip: Right-click an option to strike through it
                                    </p>
                                )}
                            </CardContent>
                        </Card>

                        {/* Action Buttons */}
                        <div className="flex justify-end gap-3">
                            {!submitted ? (
                                <Button 
                                    onClick={submitAnswer}
                                    disabled={!selectedAnswer || submitting}
                                    size="lg"
                                    data-testid="submit-answer-btn"
                                >
                                    {submitting ? (
                                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Checking...</>
                                    ) : (
                                        <>Submit Answer<ArrowRight className="ml-2 h-4 w-4" /></>
                                    )}
                                </Button>
                            ) : (
                                <Button 
                                    onClick={generateQuestion}
                                    size="lg"
                                    data-testid="next-question-btn"
                                >
                                    Next Question
                                    <ArrowRight className="ml-2 h-4 w-4" />
                                </Button>
                            )}
                        </div>

                        {/* Explanation */}
                        {submitted && (
                            <Card className={cn(
                                "border-2",
                                result?.is_correct && !isDark && "border-emerald-500 bg-emerald-100",
                                result?.is_correct && isDark && "border-emerald-500 bg-emerald-900/50",
                                !result?.is_correct && !isDark && "border-amber-500 bg-amber-100",
                                !result?.is_correct && isDark && "border-amber-500 bg-amber-900/50"
                            )}>
                                <CardHeader className="pb-2">
                                    <CardTitle className="flex items-center gap-2 text-lg" style={{ fontFamily: 'Manrope' }}>
                                        {result?.is_correct ? (
                                            <>
                                                <CheckCircle className="h-5 w-5 text-emerald-500" />
                                                <span className={isDark ? "text-emerald-300" : "text-emerald-800"}>Correct!</span>
                                            </>
                                        ) : (
                                            <>
                                                <BookOpen className="h-5 w-5 text-amber-500" />
                                                <span className={isDark ? "text-amber-300" : "text-amber-800"}>Explanation</span>
                                            </>
                                        )}
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <ExplanationRenderer
                                        text={question.explanation}
                                        className={cn(
                                            "leading-relaxed",
                                            result?.is_correct && !isDark && "text-emerald-900",
                                            result?.is_correct && isDark && "text-emerald-200",
                                            !result?.is_correct && !isDark && "text-amber-900",
                                            !result?.is_correct && isDark && "text-amber-200"
                                        )}
                                    />
                                    {!result?.is_correct && (
                                        <p className={cn("mt-3 text-sm font-medium", isDark ? "text-amber-300" : "text-amber-800")}>
                                            The correct answer was <strong>{question.correct_answer}</strong>.
                                        </p>
                                    )}
                                </CardContent>
                            </Card>
                        )}
                    </div>
                )}

                {/* Empty State */}
                {!question && !loading && (
                    <Card className="border-slate-200 border-dashed">
                        <CardContent className="p-12">
                            <div className="text-center">
                                <BookOpen className="h-16 w-16 mx-auto mb-4 text-slate-300" />
                                <h3 className="text-lg font-semibold text-slate-700 mb-2" style={{ fontFamily: 'Manrope' }}>
                                    Ready to Practice?
                                </h3>
                                <p className="text-slate-500 mb-6 max-w-md mx-auto">
                                    Select a subject and difficulty level, then click "Generate Question" to get started with NBME-style practice questions.
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>
        </Layout>
    );
}
